import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Spinner, Table } from "react-bootstrap";
import { apiCall } from "../../../../_services/apiCall";
import { sweetAlert } from "../../../../utils/UniversalFunction";
import config from "../../../../config/config";
import NodataFound from "../../../../img/no.png";
import SupplierManagementShell from "./SupplierManagementShell";
import { KpiIcon } from "../../../DataManagment/Quantative/component/KpiIcons";
import "./supplierManagementTables.css";

const cardSurface = {
  background: "var(--surface)",
  borderRadius: 12,
  border: "1px solid #e2e6ea",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)",
};

const tableCardSurface = { ...cardSurface, overflow: "visible" };

const filterBarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  alignItems: "center",
  padding: "12px 16px",
  marginBottom: 12,
  background: "var(--surface)",
  borderRadius: 12,
  border: "1px solid #e2e6ea",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};

const thStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: "var(--ink-4)",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  fontFamily: "var(--font-mono)",
  borderColor: "var(--rule)",
  background: "#f4f6f9",
};

function statusStyle(statusKey) {
  const key = (statusKey || "").toUpperCase();
  if (key === "PENDING") return { bg: "#fef9c3", color: "#a16207", border: "#fde047" };
  if (key === "ACCEPTED") return { bg: "#dcfce7", color: "#15803d", border: "#86efac" };
  if (key === "REJECTED" || key === "CANCELLED" || key === "EXPIRED")
    return { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" };
  return { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" };
}

const StatusBadge = ({ statusKey }) => {
  const s = statusStyle(statusKey);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontFamily: "var(--font-mono)",
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
      }}
    >
      {statusKey || "—"}
    </span>
  );
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normaliseRow(row) {
  return {
    id: row?.id,
    company_id: row?.company_id,
    company_name: row?.company_name ?? "",
    company_email: row?.company_email ?? "",
    status_key: row?.status_key ?? "",
    accepted_at: row?.accepted_at,
    expires_at: row?.expires_at,
    created_at: row?.created_at,
    raw: row,
  };
}

function extractList(payload) {
  if (!payload) return [];
  const nested = payload?.data;
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(payload)) return payload;
  if (nested && Array.isArray(nested.data)) return nested.data;
  return [];
}

const VIEW_ALL = "all";
const VIEW_PENDING = "pending";

const SupplierInvitationsView = () => {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState(VIEW_ALL);
  const [responding, setResponding] = useState({});

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}suppliers/my-invitations`,
        {},
        {},
        "GET",
        true,
        false
      );
      setRows(isSuccess ? extractList(data).map(normaliseRow) : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvitations(); }, []);

  const handleRespond = async (id, action) => {
    setResponding((prev) => ({ ...prev, [id]: action }));
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}suppliers/my-invitations/${id}/respond`,
        {},
        { action },
        "PUT"
      );
      if (isSuccess) {
        sweetAlert("success", action === "accept" ? "Invitation accepted." : "Invitation rejected.");
        await fetchInvitations();
      }
    } finally {
      setResponding((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const pendingCount = useMemo(() => rows.filter((r) => r.status_key === "PENDING").length, [rows]);

  const filtered = useMemo(() => {
    let list = activeView === VIEW_PENDING
      ? rows.filter((r) => r.status_key === "PENDING")
      : rows;
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.company_name, r.company_email, r.status_key].join(" ").toLowerCase().includes(q)
    );
  }, [rows, activeView, search]);

  return (
    <SupplierManagementShell
      pathname={location?.pathname}
      pageTitle="My invitations"
      totalCount={filtered.length}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            type="button"
            className={activeView === VIEW_ALL ? "kpi-btn-primary" : "kpi-btn-secondary"}
            onClick={() => { setActiveView(VIEW_ALL); setSearch(""); }}
          >
            All invitations ({rows.length})
          </button>
          <button
            type="button"
            className={activeView === VIEW_PENDING ? "kpi-btn-primary" : "kpi-btn-secondary"}
            onClick={() => { setActiveView(VIEW_PENDING); setSearch(""); }}
          >
            Pending ({pendingCount})
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: 48,
            ...cardSurface,
          }}
        >
          <div className="kpi-spinner" />
          <span style={{ fontSize: 13, color: "var(--ink-4)", fontWeight: 600 }}>
            Loading invitations…
          </span>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...cardSurface, textAlign: "center", padding: "48px 24px" }}>
          <img style={{ opacity: 0.2, maxWidth: 280, width: "100%" }} src={NodataFound} alt="" />
          <p style={{ marginTop: 16, color: "var(--ink-3)", fontSize: 14, fontWeight: 600 }}>
            No invitations received yet
          </p>
        </div>
      ) : (
        <>
          <div style={filterBarStyle}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180, maxWidth: 420 }}>
              <div
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-4)",
                  pointerEvents: "none",
                }}
              >
                <KpiIcon name="search" size={14} />
              </div>
              <input
                className="kpi-input"
                style={{ paddingLeft: 34, paddingRight: search ? 32 : 12 }}
                placeholder="Search company, email, status…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search invitations"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: 18,
                    color: "var(--ink-4)",
                    lineHeight: 1,
                    padding: 0,
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              ) : null}
            </div>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 12,
                color: "var(--ink-4)",
                fontFamily: "var(--font-mono)",
                whiteSpace: "nowrap",
              }}
            >
              {filtered.length} invitation{filtered.length === 1 ? "" : "s"}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ ...cardSurface, textAlign: "center", padding: "40px 24px" }}>
              <p style={{ color: "var(--ink-3)", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                No invitations match your search
              </p>
              <button type="button" className="kpi-btn-secondary" onClick={() => setSearch("")}>
                Clear search
              </button>
            </div>
          ) : (
            <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
              <div className="supplier-mgmt-table-scroll">
                <Table hover className="mb-0 supplier-mgmt-table">
                  <thead>
                    <tr>
                      <th className="supplier-mgmt-sr-cell" style={thStyle}>Sr</th>
                      <th style={{ ...thStyle, minWidth: 160 }}>Company</th>
                      <th style={thStyle}>Company email</th>
                      <th style={{ ...thStyle, width: 150 }}>Invited on</th>
                      <th style={{ ...thStyle, width: 150 }}>Expires</th>
                      <th style={{ ...thStyle, width: 110 }}>Status</th>
                      <th style={{ ...thStyle, minWidth: 160 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row, index) => {
                      const isPending = row.status_key === "PENDING";
                      const busy = responding[row.id];
                      return (
                        <tr key={row.id ?? index}>
                          <td className="supplier-mgmt-sr-cell">{index + 1}</td>
                          <td>
                            <div style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                              {row.company_name || "—"}
                            </div>
                          </td>
                          <td className="small">{row.company_email || "—"}</td>
                          <td className="small" style={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(row.created_at)}
                          </td>
                          <td className="small" style={{ whiteSpace: "nowrap" }}>
                            {formatDateTime(row.expires_at)}
                          </td>
                          <td>
                            <StatusBadge statusKey={row.status_key} />
                          </td>
                          <td>
                            {isPending ? (
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  type="button"
                                  className="kpi-btn-primary"
                                  style={{ padding: "4px 14px", fontSize: 12 }}
                                  disabled={Boolean(busy)}
                                  onClick={() => handleRespond(row.id, "accept")}
                                >
                                  {busy === "accept" ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    "Accept"
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="kpi-btn-secondary"
                                  style={{ padding: "4px 14px", fontSize: 12 }}
                                  disabled={Boolean(busy)}
                                  onClick={() => handleRespond(row.id, "reject")}
                                >
                                  {busy === "reject" ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    "Reject"
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: "var(--ink-4)" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}
    </SupplierManagementShell>
  );
};

export default SupplierInvitationsView;
